/**
 * tallyExport.ts — Build a Tally-importable XML file of Sales and Purchase
 * vouchers from BigxBook invoices and purchase orders.
 *
 * This is the feature CAs care about most: it lets them import a month of
 * vouchers into Tally in one click instead of re-keying every bill. The XML
 * follows Tally's "Import Data / Vouchers" envelope format.
 *
 * Accounting sign convention (Tally XML ALLLEDGERENTRIES.LIST):
 *   • Debit  → AMOUNT negative, ISDEEMEDPOSITIVE "Yes"
 *   • Credit → AMOUNT positive, ISDEEMEDPOSITIVE "No"
 * Each voucher's entries always net to zero.
 *
 * Pure — no DB, no I/O. The renderer/data-source builds the input objects.
 */

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface TallySalesInput {
  date: Date;
  voucherNumber: string;
  partyName: string;
  partyGstin?: string | null;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  roundOff: number;
  total: number;
  isCreditNote?: boolean;
  narration?: string;
}

export interface TallyPurchaseInput {
  date: Date;
  voucherNumber: string;
  supplierName: string;
  supplierGstin?: string | null;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
  narration?: string;
}

export interface TallyLedgerNames {
  sales: string;
  purchase: string;
  outputCgst: string;
  outputSgst: string;
  outputIgst: string;
  outputCess: string;
  inputCgst: string;
  inputSgst: string;
  inputIgst: string;
  roundOff: string;
  cashParty: string; // walk-in / no-name customers
}

export const DEFAULT_LEDGERS: TallyLedgerNames = {
  sales: 'Sales',
  purchase: 'Purchase',
  outputCgst: 'Output CGST',
  outputSgst: 'Output SGST',
  outputIgst: 'Output IGST',
  outputCess: 'Output Cess',
  inputCgst: 'Input CGST',
  inputSgst: 'Input SGST',
  inputIgst: 'Input IGST',
  roundOff: 'Round Off',
  cashParty: 'Cash',
};

export interface TallyExportInput {
  companyName: string;
  sales: TallySalesInput[];
  purchases: TallyPurchaseInput[];
  ledgers?: Partial<TallyLedgerNames>;
}

// ─── Primitives ──────────────────────────────────────────────────────────────

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Tally expects dates as YYYYMMDD. */
export function formatTallyDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function amt(n: number): string {
  return (Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2);
}

interface LedgerEntry {
  ledgerName: string;
  /** true = debit, false = credit. */
  debit: boolean;
  /** Always the positive magnitude; sign is derived from `debit`. */
  amount: number;
}

function renderEntry(e: LedgerEntry): string {
  const signed = e.debit ? -e.amount : e.amount;
  return [
    '        <ALLLEDGERENTRIES.LIST>',
    `          <LEDGERNAME>${escapeXml(e.ledgerName)}</LEDGERNAME>`,
    `          <ISDEEMEDPOSITIVE>${e.debit ? 'Yes' : 'No'}</ISDEEMEDPOSITIVE>`,
    `          <AMOUNT>${amt(signed)}</AMOUNT>`,
    '        </ALLLEDGERENTRIES.LIST>',
  ].join('\n');
}

function renderVoucher(spec: {
  type: string; // "Sales" | "Purchase" | "Credit Note"
  date: Date;
  number: string;
  partyLedger: string;
  partyGstin?: string | null;
  narration?: string;
  entries: LedgerEntry[];
}): string {
  const date = formatTallyDate(spec.date);
  const parts: string[] = [
    `      <VOUCHER VCHTYPE="${escapeXml(spec.type)}" ACTION="Create" OBJVIEW="Invoice Voucher View">`,
    `        <DATE>${date}</DATE>`,
    `        <EFFECTIVEDATE>${date}</EFFECTIVEDATE>`,
    `        <VOUCHERTYPENAME>${escapeXml(spec.type)}</VOUCHERTYPENAME>`,
    `        <VOUCHERNUMBER>${escapeXml(spec.number)}</VOUCHERNUMBER>`,
    `        <PARTYLEDGERNAME>${escapeXml(spec.partyLedger)}</PARTYLEDGERNAME>`,
    `        <REFERENCE>${escapeXml(spec.number)}</REFERENCE>`,
  ];
  if (spec.partyGstin) parts.push(`        <PARTYGSTIN>${escapeXml(spec.partyGstin)}</PARTYGSTIN>`);
  if (spec.narration) parts.push(`        <NARRATION>${escapeXml(spec.narration)}</NARRATION>`);
  parts.push(...spec.entries.map(renderEntry));
  parts.push('      </VOUCHER>');
  return parts.join('\n');
}

// ─── Voucher mappers ─────────────────────────────────────────────────────────

function salesEntries(s: TallySalesInput, L: TallyLedgerNames): LedgerEntry[] {
  const party = s.partyName?.trim() || L.cashParty;
  // For a sales invoice the party is debited by the total; sales + taxes are
  // credited. A credit note reverses every side.
  const flip = s.isCreditNote === true;
  const debitParty = !flip;
  const entries: LedgerEntry[] = [];
  entries.push({ ledgerName: party, debit: debitParty, amount: Math.abs(s.total) });
  const push = (name: string, value: number) => {
    if (value === 0) return;
    entries.push({ ledgerName: name, debit: flip, amount: Math.abs(value) });
  };
  push(L.sales, s.taxable);
  push(L.outputCgst, s.cgst);
  push(L.outputSgst, s.sgst);
  push(L.outputIgst, s.igst);
  push(L.outputCess, s.cess);
  if (s.roundOff !== 0) {
    // A positive round-off increases the total → credit side on a sale.
    const isCredit = s.roundOff > 0;
    entries.push({ ledgerName: L.roundOff, debit: flip ? isCredit : !isCredit, amount: Math.abs(s.roundOff) });
  }
  return entries;
}

function purchaseEntries(p: TallyPurchaseInput, L: TallyLedgerNames): LedgerEntry[] {
  // Supplier (creditor) is credited by the total; purchase + input taxes are
  // debited.
  const entries: LedgerEntry[] = [];
  entries.push({ ledgerName: p.supplierName?.trim() || 'Sundry Creditors', debit: false, amount: Math.abs(p.total) });
  const push = (name: string, value: number) => {
    if (value === 0) return;
    entries.push({ ledgerName: name, debit: true, amount: Math.abs(value) });
  };
  push(L.purchase, p.taxable);
  push(L.inputCgst, p.cgst);
  push(L.inputSgst, p.sgst);
  push(L.inputIgst, p.igst);
  return entries;
}

// ─── Public builder ──────────────────────────────────────────────────────────

export function buildTallyXml(input: TallyExportInput): string {
  const L: TallyLedgerNames = { ...DEFAULT_LEDGERS, ...(input.ledgers ?? {}) };

  const vouchers: string[] = [];

  for (const s of input.sales) {
    vouchers.push(
      renderVoucher({
        type: s.isCreditNote ? 'Credit Note' : 'Sales',
        date: s.date,
        number: s.voucherNumber,
        partyLedger: s.partyName?.trim() || L.cashParty,
        partyGstin: s.partyGstin,
        narration: s.narration,
        entries: salesEntries(s, L),
      }),
    );
  }
  for (const p of input.purchases) {
    vouchers.push(
      renderVoucher({
        type: 'Purchase',
        date: p.date,
        number: p.voucherNumber,
        partyLedger: p.supplierName?.trim() || 'Sundry Creditors',
        partyGstin: p.supplierGstin,
        narration: p.narration,
        entries: purchaseEntries(p, L),
      }),
    );
  }

  const tallyMessages = vouchers
    .map((v) => `    <TALLYMESSAGE xmlns:UDF="TallyUDF">\n${v}\n    </TALLYMESSAGE>`)
    .join('\n');

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<ENVELOPE>',
    '  <HEADER>',
    '    <TALLYREQUEST>Import Data</TALLYREQUEST>',
    '  </HEADER>',
    '  <BODY>',
    '    <IMPORTDATA>',
    '      <REQUESTDESC>',
    '        <REPORTNAME>Vouchers</REPORTNAME>',
    '        <STATICVARIABLES>',
    `          <SVCURRENTCOMPANY>${escapeXml(input.companyName)}</SVCURRENTCOMPANY>`,
    '        </STATICVARIABLES>',
    '      </REQUESTDESC>',
    '      <REQUESTDATA>',
    tallyMessages,
    '      </REQUESTDATA>',
    '    </IMPORTDATA>',
    '  </BODY>',
    '</ENVELOPE>',
  ].join('\n');
}

// ─── Validation helper (used by tests + a defensive UI check) ────────────────

/** Sum of signed amounts per voucher; should be ~0 for a balanced voucher. */
export function voucherImbalance(entries: { debit: boolean; amount: number }[]): number {
  return entries.reduce((s, e) => s + (e.debit ? -e.amount : e.amount), 0);
}

export { salesEntries as _salesEntries, purchaseEntries as _purchaseEntries };
